import { useMutation } from "@apollo/client/react";
import { Card, styled, useTheme } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccountContext } from "../../../strict/app/AccountResolver";
import type { SaveCredentialMutation, SaveCredentialMutationVariables } from "../../../graphql/types";
import { Credential, SaveCredentialDocument } from "../../../graphql/types";

import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { toast } from "react-toastify";
import { parseGQLResponseError } from "../../../graphql/util";
import { EditorFormWithMutation } from "../../../pages/keys/KeyBrowser";
import { KeyEditorActionModal } from "./KeyEditorActionModal";
import { KeyInformationFooter } from "./KeyInformationFooter";

const CardStyled = styled(Card)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "28px 40px 40px 40px",
}) as typeof Card;

type KeyInformationProps = {
  credential: Credential;
};

const KeyInformation = ({ credential }: KeyInformationProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();
  const { urlName: url_name } = useAccountContext();

  const link = `/app/${url_name}/keys/edit-key?keyName=${encodeURIComponent(credential.name)}`;

  const theme = useTheme();

  const [saveCredential] = useMutation<SaveCredentialMutation, SaveCredentialMutationVariables>(
    SaveCredentialDocument,
    {
      onCompleted: (data) => {
        if (data.save_credential?.__typename == "SaveCredentialOk") {
          toast.info(`${credential.name} was successfully deleted!`, {
            icon: <TrashIcon color={theme.palette.accentInfo30.main} width="18px" height="18px" />,
          });
          navigate(`/app/${url_name}/keys`);
        }
      },
      onError: (error) => {
        const message = parseGQLResponseError(error);
        if (message) {
          toast.error(message);
        } else {
          toast.error("An error occurred while deleting the key");
          console.error("An error occurred while deleting the key", error);
        }
      },
    }
  );
  const onClickDelete = () => {
    saveCredential({
      variables: {
        name: credential.name,
        status: "archived",
      },
    });
  };
  return (
    <CardStyled>
      <KeyEditorActionModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        body="Deleting this key may affect some pipelines. This action cannot be undone."
        buttonAction={onClickDelete}
        buttonText="Delete"
        onCancel={() => setShowDeleteModal(false)}
        title="Delete this key?"
        buttonEndIcon={<TrashIcon />}
      />
      <EditorFormWithMutation onCompleted={() => {}} credential={credential} mode={"view"} />
      <KeyInformationFooter
        onClickDelete={() => setShowDeleteModal(true)}
        onClickEditKey={() => navigate(link)}
        credential={credential}
      />
    </CardStyled>
  );
};

export { KeyInformation };
